import { useCallback, useEffect, useRef, useState } from "react";
import { searchPublicMatchesPage } from "../../lib/leaderboardApi";
import { matchKey, normalizeMatch } from "./matchDiscovery";

const empty = () => ({ matches: [], locations: [], nextToken: null, failures: [], loading: true, loadingMore: false });
export default function usePublicMatches(filters = {}, refreshDay = "") {
  const key = JSON.stringify({ filters, refreshDay });
  const [state, setState] = useState(empty);
  const current = useRef(state), generation = useRef(0), controller = useRef(null), busy = useRef(false);
  const load = useCallback(async previous => {
    const version = ++generation.current;
    controller.current?.abort();
    const abort = new AbortController(); controller.current = abort; busy.current = true;
    let next = previous ? { ...previous, failures: [], loadingMore: true } : empty();
    setState(next);
    const seen = new Set();
    try {
      // An empty filtered API page is a continuation, never proof of no matches.
      do {
        const cursor = next.nextToken;
        if (seen.has(cursor)) throw new Error("The search could not finish. Try again.");
        seen.add(cursor);
        const page = await searchPublicMatchesPage({ ...JSON.parse(key).filters, nextToken: cursor, signal: abort.signal });
        if (generation.current !== version) return;
        const unique = new Map([...next.matches, ...page.data.map(match => normalizeMatch(match, match.competitionType))].map(match => [matchKey(match), match]));
        next = { ...next, matches: [...unique.values()], locations: [...new Set([...next.locations, ...(page.locations || [])])].sort(), nextToken: page.nextToken };
        if (page.data.length || !page.nextToken) break;
        if (seen.size >= 50) throw new Error("The search is taking longer than expected. Try again to continue.");
      } while (true);
    } catch (error) {
      if (generation.current !== version || abort.signal.aborted) return;
      next.failures = [error.message || "Matches could not load"];
    }
    next = { ...next, loading: false, loadingMore: false };
    current.current = next; busy.current = false; setState(next);
  }, [key]);
  useEffect(() => {
    const requestGeneration = generation;
    load();
    return () => { requestGeneration.current++; controller.current?.abort(); busy.current = false; };
  }, [load]);
  return { ...state, hasMore: Boolean(state.nextToken), retry: () => { if (!busy.current) load(current.current); }, loadMore: () => { if (!busy.current) load(current.current); } };
}
